/**
 * Digital receptionist contracts (BUILD-PLAN M15, WP3-RECEP; library
 * `research/library/ai-receptionist.md`).
 *
 * Velora does not run the voice pipeline. The receptionist service
 * (recep.client, the Python Pipecat service) owns the media leg and calls
 * back into Velora through the runtime tool API below. Velora owns the
 * office profile, the booking tools, the after-hours rules, the call record
 * on the patient timeline and the warm-transfer status.
 *
 * Hard rules carried in these shapes (reuse guide recep-navi section 4/5):
 * - After hours the receptionist never books, cancels or reschedules. The
 *   after-hours profile has no booking tools at all, and no profile has a
 *   cancel or reschedule tool.
 * - A booking needs a read-back the caller agreed to (`ReadBackRequest`).
 * - Nothing about a caller's record is said before verification.
 * - Every greeting discloses that the caller is talking to a virtual
 *   assistant (`hasAiDisclosure`).
 * - When the office cannot write appointments to its PMS, the wording is
 *   "requested", never "booked".
 */
import { z } from 'zod';
import { E164Schema } from './patients.js';
import { IdSchema, InstantSchema, LocalDateSchema } from './sync.js';

/* ------------------------------------------------------------------------ */
/* Vocabularies                                                              */
/* ------------------------------------------------------------------------ */

/** Which rule set applies to a call: office open, or closed (after hours, holiday). */
export const HoursProfileSchema = z.enum(['business', 'after_hours']);
export type HoursProfile = z.infer<typeof HoursProfileSchema>;

/** test: summaries stay internal and calls are flagged; live: real; paused: falls to the fallback node. */
export const ReceptionistModeSchema = z.enum(['test', 'live', 'paused']);
export type ReceptionistMode = z.infer<typeof ReceptionistModeSchema>;

/** Business-hours booking: off (message only), request (approval queue) or book. */
export const BookingModeSchema = z.enum(['off', 'request', 'book']);
export type BookingMode = z.infer<typeof BookingModeSchema>;

export const TriageLevelSchema = z.enum(['routine', 'urgent', 'emergency']);
export type TriageLevel = z.infer<typeof TriageLevelSchema>;

export const CALLER_INTENTS = [
  'book',
  'confirm',
  'reschedule',
  'cancel',
  'appointment_info',
  'billing',
  'insurance',
  'faq',
  'emergency',
  'human',
  'other',
] as const;
export const CallerIntentSchema = z.enum(CALLER_INTENTS);
export type CallerIntent = z.infer<typeof CallerIntentSchema>;

/** Action chips on the call card (library 4.6). */
export const CALL_ACTIONS = [
  'booked',
  'requested',
  'change_redirected',
  'emergency_script',
  'urgent_flagged',
  'transferred',
  'transfer_failed',
  'message_taken',
  'verified',
  'verification_failed',
  'terminated_limit',
] as const;
export const CallActionSchema = z.enum(CALL_ACTIONS);
export type CallAction = z.infer<typeof CallActionSchema>;

export const TransferStatusSchema = z.enum([
  'requested',
  'ringing',
  'answered',
  'no_answer',
  'failed',
  'cancelled',
]);
export type TransferStatus = z.infer<typeof TransferStatusSchema>;

/** Call follow-up status (recep portal vocabulary). The first three are open. */
export const FOLLOW_UP_STATUSES = [
  'needs_callback',
  'no_answer',
  'voicemail',
  'reached',
  'scheduled',
  'resolved',
  'unreachable',
] as const;
export const FollowUpStatusSchema = z.enum(FOLLOW_UP_STATUSES);
export type FollowUpStatus = z.infer<typeof FollowUpStatusSchema>;
export const OPEN_FOLLOW_UPS: readonly FollowUpStatus[] = [
  'needs_callback',
  'no_answer',
  'voicemail',
];

export const CallEndReasonSchema = z.enum([
  'caller_hangup',
  'completed',
  'transferred',
  'limit_duration',
  'limit_unproductive',
  'error',
]);
export type CallEndReason = z.infer<typeof CallEndReasonSchema>;

/** Tools the receptionist may call; the profile decides which are registered. */
export const RECEPTIONIST_TOOLS = [
  'lookup_patient',
  'find_slots',
  'hold_slot',
  'read_back',
  'confirm_booking',
  'take_message',
  'transfer',
  'office_info',
] as const;
export const ReceptionistToolSchema = z.enum(RECEPTIONIST_TOOLS);
export type ReceptionistTool = z.infer<typeof ReceptionistToolSchema>;

/* ------------------------------------------------------------------------ */
/* Disclosure and fixed lines                                                */
/* ------------------------------------------------------------------------ */

/** Accepted disclosure wording; a greeting must contain one (library AR-12, AR-22). */
export const AI_DISCLOSURE_PHRASES = [
  'virtual assistant',
  'automated assistant',
  'ai assistant',
  'ai receptionist',
  'virtual receptionist',
] as const;

export function hasAiDisclosure(greeting: string): boolean {
  const g = greeting.toLowerCase().replace(/\s+/g, ' ');
  return AI_DISCLOSURE_PHRASES.some((p) => g.includes(p));
}

export const DISCLOSURE_REQUIRED_MESSAGE = 'An AI disclosure is required';
export const FALLBACK_REQUIRED_MESSAGE = 'AI Receptionist needs a fallback';

/* ------------------------------------------------------------------------ */
/* Profile (one per office)                                                  */
/* ------------------------------------------------------------------------ */

export const FallbackNodeSchema = z.object({
  kind: z.enum(['ring_group', 'voicemail', 'on_call_cell']),
  /** Extension, ring group number or mailbox. */
  target: z.string().trim().min(1).max(40),
  label: z.string().trim().min(1).max(80),
});
export type FallbackNode = z.infer<typeof FallbackNodeSchema>;

export const TransferTargetSchema = z.object({
  label: z.string().trim().min(1).max(80),
  extension: z.string().regex(/^\d{2,6}$/, 'extension must be 2 to 6 digits'),
});
export type TransferTarget = z.infer<typeof TransferTargetSchema>;

const KeywordSchema = z.string().trim().min(2).max(60);

export const CapabilityViewSchema = z.object({
  enabled: z.boolean(),
  reason: z.string().nullable(),
});
export type CapabilityView = z.infer<typeof CapabilityViewSchema>;

export const ReceptionistProfileSchema = z.object({
  locationId: IdSchema,
  officeName: z.string(),
  timezone: z.string(),
  mode: ReceptionistModeSchema,
  personaName: z.string(),
  greeting: z.string(),
  bookingMode: BookingModeSchema,
  transferTarget: TransferTargetSchema.nullable(),
  transferTimeoutSec: z.number().int(),
  fallback: FallbackNodeSchema.nullable(),
  maxCallMinutes: z.number().int(),
  maxUnproductiveTurns: z.number().int(),
  emergencyKeywords: z.array(z.string()),
  /** The fixed after-hours redirect line, spoken by code. Not editable. */
  afterHoursChangeLine: z.string(),
  /** The fixed emergency script, spoken verbatim by code. Not editable. */
  emergencyScript: z.string(),
  runtime: z.object({
    keyIssuedAt: InstantSchema.nullable(),
    keyHint: z.string().nullable(),
    serviceUserConfigured: z.boolean(),
    lastSeenAt: InstantSchema.nullable(),
  }),
  testsPassedAt: InstantSchema.nullable(),
  /** Whether bookings can be written to this office's PMS right now. */
  appointmentWrite: CapabilityViewSchema,
  /** Whether the call summary can be written to the PMS record. */
  noteWrite: CapabilityViewSchema,
  hoursNow: z.lazy(() => HoursStatusSchema),
  version: z.number().int(),
});
export type ReceptionistProfile = z.infer<typeof ReceptionistProfileSchema>;

export const ReceptionistProfilePatchSchema = z
  .object({
    expectedVersion: z.number().int().positive(),
    personaName: z.string().trim().min(1).max(40).optional(),
    greeting: z.string().trim().min(10).max(400).optional(),
    bookingMode: BookingModeSchema.optional(),
    transferTarget: TransferTargetSchema.nullable().optional(),
    transferTimeoutSec: z.number().int().min(10).max(90).optional(),
    fallback: FallbackNodeSchema.nullable().optional(),
    maxCallMinutes: z.number().int().min(3).max(30).optional(),
    maxUnproductiveTurns: z.number().int().min(2).max(8).optional(),
    emergencyKeywords: z.array(KeywordSchema).max(80).optional(),
  })
  .strict()
  .superRefine((p, ctx) => {
    if (p.greeting !== undefined && !hasAiDisclosure(p.greeting)) {
      ctx.addIssue({ code: 'custom', path: ['greeting'], message: DISCLOSURE_REQUIRED_MESSAGE });
    }
    if (p.fallback === null) {
      ctx.addIssue({ code: 'custom', path: ['fallback'], message: FALLBACK_REQUIRED_MESSAGE });
    }
  });
export type ReceptionistProfilePatch = z.infer<typeof ReceptionistProfilePatchSchema>;

export const ModeChangeSchema = z.object({
  mode: ReceptionistModeSchema,
  expectedVersion: z.number().int().positive(),
});
export type ModeChange = z.infer<typeof ModeChangeSchema>;

/** Issued once; only the hint is stored readable. */
export const RuntimeKeySchema = z.object({ key: z.string(), hint: z.string() });
export type RuntimeKey = z.infer<typeof RuntimeKeySchema>;

/* ------------------------------------------------------------------------ */
/* Hours                                                                     */
/* ------------------------------------------------------------------------ */

/**
 * Whether the office is open right now, from the one shared hours rule
 * (`hoursState` in @velora/telephony-gateway). `not_set` when the office has
 * no hours at all: Phones shows "Hours not set" instead of "Open", and the
 * receptionist uses the after-hours profile (no booking) until hours exist.
 */
export const OfficeHoursStateSchema = z.enum(['open', 'closed', 'not_set']);
export type OfficeHoursState = z.infer<typeof OfficeHoursStateSchema>;

export const HoursStatusSchema = z.object({
  profile: HoursProfileSchema,
  state: OfficeHoursStateSchema,
  /** Plain-language reason: "Open until 5:00 PM", "Closed for Thanksgiving". */
  reason: z.string(),
  /** Next change of profile, if within 8 days. */
  changesAt: InstantSchema.nullable(),
  localDate: LocalDateSchema,
});
export type HoursStatus = z.infer<typeof HoursStatusSchema>;

/* ------------------------------------------------------------------------ */
/* Calls                                                                     */
/* ------------------------------------------------------------------------ */

export const TranscriptTurnSchema = z.object({
  speaker: z.enum(['ai', 'caller', 'staff', 'system']),
  text: z.string(),
  /** Seconds from the start of the call. */
  atSec: z.number().nonnegative(),
});
export type TranscriptTurn = z.infer<typeof TranscriptTurnSchema>;

export const CallEventKindSchema = z.enum([
  'started',
  'greeting',
  'intent',
  'triage',
  'tool',
  'tool_refused',
  'guard',
  'transfer',
  'summary',
  'ended',
  'writeback',
  'follow_up',
]);
export type CallEventKind = z.infer<typeof CallEventKindSchema>;

export const CallEventSchema = z.object({
  id: IdSchema,
  kind: CallEventKindSchema,
  label: z.string(),
  detail: z.record(z.string(), z.unknown()),
  at: InstantSchema,
});
export type CallEvent = z.infer<typeof CallEventSchema>;

export const TransferViewSchema = z.object({
  id: IdSchema,
  status: TransferStatusSchema,
  targetLabel: z.string(),
  targetExtension: z.string(),
  reason: z.string(),
  whisper: z.string(),
  requestedAt: InstantSchema,
  answeredAt: InstantSchema.nullable(),
  answeredByName: z.string().nullable(),
  endedAt: InstantSchema.nullable(),
  /** Callback promised to the caller when nobody answered. */
  callbackBy: InstantSchema.nullable(),
  taskId: IdSchema.nullable(),
});
export type TransferView = z.infer<typeof TransferViewSchema>;

/**
 * Who a caller probably is, from the phone number alone (staff-only: the
 * voice agent never says any of this to an unverified caller).
 *   verified  the caller verified on the call (patient_id set) or staff attached them
 *   matched   exactly one patient has this number (office first, then practice)
 *   family    several patients share it (a family phone): pick one
 *   new       no patient has it
 *   hidden    no usable caller ID (blocked, lost in a forward, web test)
 */
export const CallerMatchSchema = z.object({
  kind: z.enum(['verified', 'matched', 'family', 'new', 'hidden']),
  /** The one patient (verified / matched). */
  patientId: IdSchema.nullable(),
  /** "Jane Doe" for one patient; "Family phone: Smith, 5 patients" for a family. PHI. */
  label: z.string(),
  /** Short note under the name: "Matched by phone (not verified on the call)". */
  note: z.string().nullable(),
  /** Patients with the number at the call's office (else practice-wide). */
  count: z.number().int().nonnegative(),
  /** True when the matches are at the call's office; false: other offices only. */
  atOffice: z.boolean(),
  /** For `family`: the patients to pick from (office first). PHI. */
  candidates: z
    .array(z.object({ id: IdSchema, name: z.string(), birthYear: z.number().int().nullable() }))
    .default([]),
});
export type CallerMatch = z.infer<typeof CallerMatchSchema>;

export const AttachCallerSchema = z.object({ patientId: IdSchema }).strict();
export type AttachCaller = z.infer<typeof AttachCallerSchema>;

export const CallListItemSchema = z.object({
  id: IdSchema,
  locationId: IdSchema,
  startedAt: InstantSchema,
  durationSec: z.number().int().nullable(),
  callerE164: z.string().nullable(),
  callerLabel: z.string(),
  patientId: IdSchema.nullable(),
  patientName: z.string().nullable(),
  hoursProfile: HoursProfileSchema,
  test: z.boolean(),
  triage: TriageLevelSchema,
  intents: z.array(CallerIntentSchema),
  actions: z.array(CallActionSchema),
  transferStatus: TransferStatusSchema.nullable(),
  followUp: FollowUpStatusSchema.nullable(),
  summary: z.string().nullable(),
  firstUtterance: z.string().nullable(),
  live: z.boolean(),
  /** Who the caller probably is from the number (staff-only). */
  callerMatch: CallerMatchSchema.nullable().default(null),
});
export type CallListItem = z.infer<typeof CallListItemSchema>;

export const CallListQuerySchema = z.object({
  view: z.enum(['all', 'open', 'emergency', 'after_hours', 'transfers', 'test']).default('all'),
  days: z.coerce.number().int().min(1).max(90).default(7),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type CallListQuery = z.infer<typeof CallListQuerySchema>;

export const CallListSchema = z.object({ items: z.array(CallListItemSchema) });
export type CallList = z.infer<typeof CallListSchema>;

export const BookingViewSchema = z.object({
  id: IdSchema,
  status: z.enum(['pending', 'approved', 'rejected', 'booked', 'expired']),
  kind: z.enum(['booked', 'requested']),
  startsAt: InstantSchema,
  lengthMin: z.number().int(),
  appointmentTypeName: z.string().nullable(),
  providerName: z.string().nullable(),
  operatoryName: z.string().nullable(),
  commandStatus: z.string().nullable(),
});
export type BookingView = z.infer<typeof BookingViewSchema>;

export const CallDetailSchema = CallListItemSchema.extend({
  verified: z.boolean(),
  verificationMethod: z.enum(['name_dob', 'otp']).nullable(),
  endReason: CallEndReasonSchema.nullable(),
  disclosurePlayed: z.boolean(),
  language: z.string(),
  transcript: z.array(TranscriptTurnSchema),
  events: z.array(CallEventSchema),
  transfers: z.array(TransferViewSchema),
  bookings: z.array(BookingViewSchema),
  message: z.string().nullable(),
  /** PMS write-back of the summary (OD commlog / Dentrix journal), if any. */
  writeBack: z.object({
    state: z.enum(['queued', 'applied', 'failed', 'unavailable', 'not_applicable']),
    reason: z.string().nullable(),
  }),
  followUpHistory: z.array(
    z.object({
      status: FollowUpStatusSchema,
      note: z.string().nullable(),
      byName: z.string(),
      at: InstantSchema,
    }),
  ),
});
export type CallDetail = z.infer<typeof CallDetailSchema>;

export const FollowUpUpdateSchema = z.object({
  status: FollowUpStatusSchema,
  note: z.string().trim().max(500).optional(),
});
export type FollowUpUpdate = z.infer<typeof FollowUpUpdateSchema>;

/* ------------------------------------------------------------------------ */
/* Stats: per office and combined (ratio of sums)                            */
/* ------------------------------------------------------------------------ */

export const ReceptionistStatPartsSchema = z.object({
  calls: z.number().int(),
  afterHours: z.number().int(),
  booked: z.number().int(),
  requested: z.number().int(),
  transfersRequested: z.number().int(),
  transfersAnswered: z.number().int(),
  /** Calls triaged urgent or emergency (the call log's Urgent view). */
  emergencies: z.number().int(),
  openFollowUps: z.number().int(),
  talkSeconds: z.number().int(),
  changeRedirected: z.number().int(),
});
export type ReceptionistStatParts = z.infer<typeof ReceptionistStatPartsSchema>;

export const ReceptionistStatsSchema = z.object({
  days: z.number().int(),
  combined: ReceptionistStatPartsSchema.extend({
    transferSuccess: z.number().nullable(),
    avgDurationSec: z.number().nullable(),
  }),
  offices: z.array(
    ReceptionistStatPartsSchema.extend({
      locationId: IdSchema,
      officeName: z.string(),
      transferSuccess: z.number().nullable(),
      avgDurationSec: z.number().nullable(),
    }),
  ),
  /** Calls per local hour (0..23) and weekday (0 = Sunday), combined. */
  heat: z.array(
    z.object({ weekday: z.number().int(), hour: z.number().int(), calls: z.number().int() }),
  ),
});
export type ReceptionistStats = z.infer<typeof ReceptionistStatsSchema>;

/* ------------------------------------------------------------------------ */
/* Booking requests (approval queue)                                         */
/* ------------------------------------------------------------------------ */

export const BookingRequestItemSchema = z.object({
  id: IdSchema,
  locationId: IdSchema,
  callId: IdSchema.nullable(),
  patientId: IdSchema.nullable(),
  patientName: z.string().nullable(),
  callerE164: z.string().nullable(),
  startsAt: InstantSchema,
  lengthMin: z.number().int(),
  appointmentTypeName: z.string().nullable(),
  providerName: z.string().nullable(),
  operatoryName: z.string().nullable(),
  status: z.enum(['pending', 'approved', 'rejected', 'booked', 'expired']),
  reason: z.string().nullable(),
  /**
   * False when the caller gave a preference ("Tuesday mornings") rather than
   * an offered time: `startsAt` is then only the start of the preferred day.
   */
  exactTime: z.boolean(),
  /** The caller's own words for when they would like to come in. */
  timePreference: z.string().nullable(),
  /** Filed by the receptionist service after hours or outside a tool call. */
  fromService: z.boolean(),
  /** Filed by a test call: nothing was promised to a real patient. */
  test: z.boolean(),
  createdAt: InstantSchema,
});
export type BookingRequestItem = z.infer<typeof BookingRequestItemSchema>;

export const BookingRequestListSchema = z.object({ items: z.array(BookingRequestItemSchema) });

export const BookingRequestDecisionSchema = z.object({
  decision: z.enum(['booked', 'rejected']),
  note: z.string().trim().max(500).optional(),
});
export type BookingRequestDecision = z.infer<typeof BookingRequestDecisionSchema>;

/* ------------------------------------------------------------------------ */
/* Test harness (library 7.3.2, AR-20)                                       */
/* ------------------------------------------------------------------------ */

export const TEST_SCENARIOS = [
  'new_patient_booking',
  'existing_patient_reschedule',
  'after_hours_cancel',
  'urgent_pain',
  'red_flag_emergency',
  'balance_unverified',
  'human_transfer',
  'disclosure',
] as const;
export const TestScenarioSchema = z.enum(TEST_SCENARIOS);
export type TestScenario = z.infer<typeof TestScenarioSchema>;

export const TestCheckSchema = z.object({
  id: z.string(),
  label: z.string(),
  passed: z.boolean(),
  detail: z.string().nullable(),
});
export type TestCheck = z.infer<typeof TestCheckSchema>;

export const TestRunResultSchema = z.object({
  scenario: TestScenarioSchema,
  title: z.string(),
  hoursProfile: HoursProfileSchema,
  passed: z.boolean(),
  checks: z.array(TestCheckSchema),
  callId: IdSchema.nullable(),
  transcript: z.array(TranscriptTurnSchema),
});
export type TestRunResult = z.infer<typeof TestRunResultSchema>;

export const TestSuiteResultSchema = z.object({
  ranAt: InstantSchema,
  passed: z.boolean(),
  results: z.array(TestRunResultSchema),
  profileVersion: z.number().int(),
});
export type TestSuiteResult = z.infer<typeof TestSuiteResultSchema>;

export const TestRunRequestSchema = z.object({
  scenarios: z.array(TestScenarioSchema).min(1).optional(),
});

/* ------------------------------------------------------------------------ */
/* Runtime tool API (called by the receptionist service)                     */
/* ------------------------------------------------------------------------ */

/** Header carrying the office runtime key: `Authorization: Bearer <key>`. */
export const RUNTIME_KEY_PREFIX = 'vrk_';

export const CallStartRequestSchema = z.object({
  provider: z.enum(['stratus', 'threecx', 'mock']),
  providerCallId: z.string().min(1).max(200),
  callerE164: E164Schema.nullable(),
  /** Caller ID was blocked, or replaced by the office's own number in a forward. */
  callerIdStatus: z.enum(['ok', 'blocked', 'lost_in_forward']).default('ok'),
  startedAt: InstantSchema.optional(),
  language: z.string().min(2).max(10).default('en'),
  test: z.boolean().default(false),
});
export type CallStartRequest = z.infer<typeof CallStartRequestSchema>;

export const CallStartResponseSchema = z.object({
  callId: IdSchema,
  hours: HoursStatusSchema,
  mode: ReceptionistModeSchema,
  /** Tools registered for this call; after hours there is no booking tool. */
  tools: z.array(ReceptionistToolSchema),
  greeting: z.string(),
  personaName: z.string(),
  /** Number of patients on the caller's number. Never said to the caller. */
  callerIdMatches: z.number().int(),
});
export type CallStartResponse = z.infer<typeof CallStartResponseSchema>;

export const TurnRequestSchema = z.object({
  speaker: z.enum(['ai', 'caller']),
  text: z.string().min(1).max(4000),
  atSec: z.number().nonnegative(),
});
export type TurnRequest = z.infer<typeof TurnRequestSchema>;

/**
 * What code decided about a caller turn. When `say` is set the service must
 * speak it verbatim and skip the LLM for this turn (emergency script, the
 * after-hours change line, abuse wrap-up).
 */
export const TurnDecisionSchema = z.object({
  triage: TriageLevelSchema,
  triageLabels: z.array(z.string()),
  intents: z.array(CallerIntentSchema),
  say: z.string().nullable(),
  sayReason: z
    .enum(['emergency_script', 'change_redirect', 'limit', 'transfer_offer', 'screening'])
    .nullable(),
  endCall: z.boolean(),
  /** Suggested tool for the service's dialog manager (never mandatory). */
  next: z.enum(['continue', 'transfer', 'take_message', 'end']).default('continue'),
});
export type TurnDecision = z.infer<typeof TurnDecisionSchema>;

/** Text the service wants to speak; checked by the PHI guard before TTS. */
export const SpeakCheckRequestSchema = z.object({
  text: z.string().min(1).max(4000),
  /** Record fields the text was built from (balance, appointment, ...). */
  fields: z.array(z.string()).default([]),
});
export const SpeakCheckResponseSchema = z.object({
  allowed: z.boolean(),
  reason: z.string().nullable(),
  /** Safe replacement when not allowed. */
  replacement: z.string().nullable(),
});

export const LookupRequestSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  birthdate: LocalDateSchema,
});
export type LookupRequest = z.infer<typeof LookupRequestSchema>;

export const LookupResponseSchema = z.discriminatedUnion('verified', [
  z.object({
    verified: z.literal(true),
    patientFirstName: z.string(),
    /** Next scheduled visits, spoken form only (no provider last names beyond Dr.). */
    upcoming: z.array(z.object({ say: z.string(), startsAt: InstantSchema })),
    triesLeft: z.number().int(),
  }),
  z.object({
    verified: z.literal(false),
    /** Fixed non-disclosing line; never confirms or denies a record exists. */
    say: z.string(),
    triesLeft: z.number().int(),
    takeMessage: z.boolean(),
  }),
]);
export type LookupResponse = z.infer<typeof LookupResponseSchema>;

export const FindSlotsRequestSchema = z.object({
  /** Words the caller used ("cleaning", "toothache"); matched to appointment types. */
  reason: z.string().trim().min(1).max(200),
  newPatient: z.boolean().default(false),
  preferredProviderId: IdSchema.optional(),
  /** Office-local date to start searching from (default: today). */
  fromDate: LocalDateSchema.optional(),
  partOfDay: z.enum(['any', 'morning', 'afternoon']).default('any'),
  limit: z.number().int().min(1).max(3).default(2),
});
export type FindSlotsRequest = z.infer<typeof FindSlotsRequestSchema>;

export const SlotOfferSchema = z.object({
  slotId: z.string(),
  startsAt: InstantSchema,
  lengthMin: z.number().int(),
  appointmentTypeId: IdSchema,
  appointmentTypeName: z.string(),
  providerId: IdSchema,
  providerName: z.string(),
  operatoryId: IdSchema,
  /** "Tuesday, October 13 at 9:00 AM with Dr. Patel". */
  say: z.string(),
});
export type SlotOffer = z.infer<typeof SlotOfferSchema>;

export const FindSlotsResponseSchema = z.object({
  appointmentTypeName: z.string().nullable(),
  offers: z.array(SlotOfferSchema),
  /** "or a different day?" is always offered; null when nothing matched. */
  say: z.string(),
});
export type FindSlotsResponse = z.infer<typeof FindSlotsResponseSchema>;

export const HoldRequestSchema = z.object({ slotId: z.string().min(1).max(400) });
export const HoldResponseSchema = z.object({
  holdId: IdSchema,
  expiresAt: InstantSchema,
  /** The read-back the service must speak before confirming. */
  readBack: z.string(),
  /** "booked" or "requested": the office's real write capability. */
  outcomeIfConfirmed: z.enum(['booked', 'requested']),
});
export type HoldResponse = z.infer<typeof HoldResponseSchema>;

export const ReadBackRequestSchema = z.object({
  holdId: IdSchema,
  /** The exact text the service spoke. Must contain the read-back. */
  spoken: z.string().min(1).max(1000),
  /** The caller's reply to the read-back. */
  callerReply: z.string().min(1).max(500),
});
export type ReadBackRequest = z.infer<typeof ReadBackRequestSchema>;

export const ReadBackResponseSchema = z.object({
  accepted: z.boolean(),
  reason: z.string().nullable(),
});

export const ConfirmBookingRequestSchema = z.object({
  holdId: IdSchema,
  /** Required for a caller who is not on file (new patient request). */
  newPatient: z
    .object({
      firstName: z.string().trim().min(1).max(80),
      lastName: z.string().trim().min(1).max(80),
      birthdate: LocalDateSchema.optional(),
    })
    .optional(),
});
export type ConfirmBookingRequest = z.infer<typeof ConfirmBookingRequestSchema>;

export const ConfirmBookingResponseSchema = z.object({
  outcome: z.enum(['booked', 'requested']),
  bookingRequestId: IdSchema,
  /** The wrap-up to speak; says "requested" whenever outcome is requested. */
  say: z.string(),
  /** Confirmation text body (sent only when SMS is enabled for the office). */
  sms: z.string(),
});
export type ConfirmBookingResponse = z.infer<typeof ConfirmBookingResponseSchema>;

export const TakeMessageRequestSchema = z.object({
  callerName: z.string().trim().min(1).max(120),
  callbackE164: E164Schema.nullable(),
  message: z.string().trim().min(1).max(2000),
  reason: z.enum(['general', 'billing', 'insurance', 'new_patient', 'clinical', 'other']),
});
export type TakeMessageRequest = z.infer<typeof TakeMessageRequestSchema>;
export const TakeMessageResponseSchema = z.object({
  taskId: IdSchema.nullable(),
  leadId: IdSchema.nullable(),
  say: z.string(),
});

export const TransferRequestSchema = z.object({
  reason: z.string().trim().min(1).max(200),
});
export const TransferStartResponseSchema = z.object({
  transferId: IdSchema,
  /** The whisper played to the answering staff member (no more than 6 s of speech). */
  whisper: z.string(),
  targetExtension: z.string(),
  timeoutSec: z.number().int(),
  /** Line spoken to the caller while they wait. */
  say: z.string(),
});
export type TransferStartResponse = z.infer<typeof TransferStartResponseSchema>;

export const TransferUpdateSchema = z.object({
  status: z.enum(['ringing', 'answered', 'no_answer', 'failed', 'cancelled']),
  answeredByExtension: z
    .string()
    .regex(/^\d{2,6}$/)
    .optional(),
});
export const TransferUpdateResponseSchema = z.object({
  status: TransferStatusSchema,
  /** Fail-back line when nobody answered: a callback promise with a time. */
  say: z.string().nullable(),
  taskId: IdSchema.nullable(),
});
export type TransferUpdateResponse = z.infer<typeof TransferUpdateResponseSchema>;

export const CallEndRequestSchema = z.object({
  reason: CallEndReasonSchema,
  endedAt: InstantSchema.optional(),
  /** Post-call summary from the service's summariser (local model only). */
  summary: z.string().trim().max(2000).optional(),
});
export const CallEndResponseSchema = z.object({
  callId: IdSchema,
  actions: z.array(CallActionSchema),
  followUp: FollowUpStatusSchema.nullable(),
  summary: z.string(),
});
export type CallEndResponse = z.infer<typeof CallEndResponseSchema>;

/** Error codes the runtime API answers with, for the service's dialog manager. */
export const RUNTIME_ERROR_CODES = [
  'tool_not_available',
  'read_back_required',
  'hold_expired',
  'slot_taken',
  'not_verified',
  'call_ended',
] as const;

/* ------------------------------------------------------------------------ */
/* Receptionist service (recep.client) status                                */
/* ------------------------------------------------------------------------ */

export const ServiceHealthSchema = z.object({
  /**
   * http: Velora can call the service; inbound: Velora cannot call it, but
   * this office receives signed reports from it; mock: neither.
   */
  kind: z.enum(['http', 'mock', 'inbound']),
  reachable: z.boolean(),
  status: z.string(),
  version: z.string().nullable(),
  liveCalls: z.number().int().nullable(),
  maxLiveCalls: z.number().int().nullable(),
});
export type ServiceHealth = z.infer<typeof ServiceHealthSchema>;

export const RingTestRequestSchema = z.object({ toE164: E164Schema });
export const RingTestResponseSchema = z.object({ accepted: z.boolean(), message: z.string() });

/** What the receptionist service loads for an office (GET /runtime/profile). */
export const RuntimeProfileSchema = z.object({
  locationId: IdSchema,
  officeName: z.string(),
  timezone: z.string(),
  mode: ReceptionistModeSchema,
  personaName: z.string(),
  greeting: z.string(),
  hours: HoursStatusSchema,
  /** Tools registered if a call started now. */
  tools: z.array(ReceptionistToolSchema),
  emergencyKeywords: z.array(z.string()),
  afterHoursChangeLine: z.string(),
  emergencyScript: z.string(),
  maxCallMinutes: z.number().int(),
  maxUnproductiveTurns: z.number().int(),
  transferTimeoutSec: z.number().int(),
  fallback: FallbackNodeSchema.nullable(),
  /** Per-tenant BAA flags are not set: summaries and STT/LLM/TTS stay on-prem. */
  inference: z.literal('local_only'),
});
export type RuntimeProfile = z.infer<typeof RuntimeProfileSchema>;

export const ProfileEnvelopeSchema = z.object({
  profile: ReceptionistProfileSchema.nullable(),
});
export type ProfileEnvelope = z.infer<typeof ProfileEnvelopeSchema>;

/* ------------------------------------------------------------------------ */
/* Receptionist service integration (T7d)                                    */
/* ------------------------------------------------------------------------ */
//
// The running receptionist (recep.client / navi-voice) does not drive the
// per-turn runtime API above. It finishes a call, then tells Velora what
// happened; during a call it may ask for openings and file a booking
// request. Every request is signed per office:
//
//   X-Velora-Office:    <the office's Velora location id>
//   X-Velora-Signature: t=<unix seconds>,v1=<hex HMAC-SHA256(secret, `${t}.${rawBody}`)>
//
// The secret (`vrs_...`) is issued once per office on the Receptionist
// screen. Requests older or newer than 5 minutes are refused, and every body
// carries a unique `id`: a repeated id with the same body gets the first
// answer back (safe retries, nothing is written twice); a repeated id with
// a different body is refused.

export const INTEGRATION_OFFICE_HEADER = 'x-velora-office';
export const INTEGRATION_SIGNATURE_HEADER = 'x-velora-signature';
export const INTEGRATION_SECRET_PREFIX = 'vrs_';
export const INTEGRATION_TOLERANCE_SECONDS = 300;

/** The receptionist service's own name for the office (recep `tenant_id`). */
export const ServiceTenantIdSchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9][a-z0-9-]{1,62}$/, 'lowercase letters, digits and dashes');

const ExternalCallIdSchema = z.string().trim().min(1).max(200);

export const InboundTranscriptTurnSchema = z.object({
  speaker: z.enum(['ai', 'caller']),
  text: z.string().trim().min(1).max(4000),
  /** Seconds from the start of the call, when the service knows it. */
  atSec: z.number().nonnegative().max(36_000).optional(),
});
export type InboundTranscriptTurn = z.infer<typeof InboundTranscriptTurnSchema>;

/** The service's end-of-call extraction (recep `extract_voice_lead` field set). */
export const InboundLeadSchema = z.object({
  firstName: z.string().trim().max(80).default(''),
  lastName: z.string().trim().max(80).default(''),
  callbackE164: E164Schema.nullable().default(null),
  newOrExisting: z.enum(['new', 'existing', 'unknown']).default('unknown'),
  requestType: z
    .enum(['appointment', 'insurance', 'forms', 'emergency', 'update_info', 'general_question'])
    .nullable()
    .default(null),
  /** One sentence: what the caller wants. */
  request: z.string().trim().max(500).default(''),
  preferredTimes: z.string().trim().max(200).nullable().default(null),
  insurance: z.string().trim().max(120).nullable().default(null),
  notes: z.string().trim().max(500).nullable().default(null),
  /** For the dentist: only what the caller said about symptoms. */
  clinicalSummary: z.string().trim().max(500).nullable().default(null),
});
export type InboundLead = z.infer<typeof InboundLeadSchema>;

export const CallCompletedEventSchema = z
  .object({
    id: IdSchema,
    type: z.literal('call.completed'),
    occurredAt: InstantSchema,
    call: z.object({
      externalCallId: ExternalCallIdSchema,
      channel: z.enum(['phone', 'web']),
      startedAt: InstantSchema,
      endedAt: InstantSchema,
      callerE164: E164Schema.nullable(),
      callerIdStatus: z.enum(['ok', 'blocked', 'lost_in_forward']).default('ok'),
      forwardedFromE164: E164Schema.nullable().default(null),
      /** Carrier label for the line; never proof of who called. */
      cnam: z.string().trim().max(60).nullable().default(null),
      /** The service's own test flag (test mode or a staff test call). */
      test: z.boolean(),
      endReason: CallEndReasonSchema,
      /** What the service believed; Velora decides from its own hours and records both. */
      serviceAfterHours: z.boolean(),
      /** The service's deterministic triage. Velora re-runs its own rules and keeps the higher. */
      triage: z.object({
        level: TriageLevelSchema,
        labels: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
      }),
      summary: z.string().trim().max(2000).default(''),
      lead: InboundLeadSchema.nullable().default(null),
      transcript: z.array(InboundTranscriptTurnSchema).max(600),
      language: z.string().min(2).max(10).default('en'),
      /**
       * Where the transcript stays when the service does not send it (PHI stays
       * on the receptionist box): an opaque reference, never the text.
       */
      transcriptRef: z
        .string()
        .trim()
        .regex(/^recep:[a-z0-9][a-z0-9-]{1,62}\/\d{4}-\d{2}-\d{2}\/[A-Za-z0-9_.-]{1,120}$/)
        .nullable()
        .default(null),
      /** The service relayed the caller's message to the office or on-call doctor. */
      relayed: z.boolean().nullable().default(null),
      /** The carrier's call id (Twilio CallSid) when the service knows it. */
      providerCallSid: z
        .string()
        .regex(/^CA[0-9a-f]{32}$/)
        .nullable()
        .default(null),
      /** Caller turns, when the transcript itself is withheld. */
      callerTurns: z.number().int().nonnegative().max(1000).nullable().default(null),
      /**
       * A call reported late, from the service's history (W5, 2026-10-28): it is
       * recorded like any other call (call log, AI receptionist list, Leads for a
       * new caller), but it adds no worklist task, because the office was already
       * told at the time by the service's own email or alert.
       */
      backfill: z.boolean().default(false),
    }),
  })
  .refine((e) => Date.parse(e.call.endedAt) >= Date.parse(e.call.startedAt), {
    path: ['call', 'endedAt'],
    message: 'endedAt is before startedAt',
  });
export type CallCompletedEvent = z.infer<typeof CallCompletedEventSchema>;

export const CallCompletedResponseSchema = z.object({
  received: z.literal(true),
  /** True when this event id was already processed; the first answer is returned. */
  duplicate: z.boolean(),
  callId: IdSchema,
  followUp: FollowUpStatusSchema.nullable(),
  taskId: IdSchema.nullable(),
  leadId: IdSchema.nullable(),
});
export type CallCompletedResponse = z.infer<typeof CallCompletedResponseSchema>;

const BookingRequestBodySchema = z.object({
  id: IdSchema,
  occurredAt: InstantSchema,
  /** The service's call id, so the request joins the call card when it arrives. */
  externalCallId: ExternalCallIdSchema.nullable().default(null),
  test: z.boolean().default(false),
  caller: z.object({
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    birthdate: LocalDateSchema.nullable().default(null),
    callbackE164: E164Schema.nullable(),
    newPatient: z.boolean(),
  }),
  /** The caller's words for the visit ("cleaning", "my crown came off"). */
  reason: z.string().trim().min(1).max(200),
  /** An offer from `availability`. Still only a request: staff confirm it. */
  slotId: z.string().min(1).max(400).nullable().default(null),
  /** When no offered time fits: the caller's preference in their own words. */
  preference: z
    .object({
      fromDate: LocalDateSchema.nullable().default(null),
      partOfDay: z.enum(['any', 'morning', 'afternoon']).default('any'),
      text: z.string().trim().min(1).max(200),
    })
    .nullable()
    .default(null),
});

export const BookingRequestedEventSchema = BookingRequestBodySchema.extend({
  type: z.literal('booking.requested'),
}).refine((b) => b.slotId !== null || b.preference !== null, {
  path: ['preference'],
  message: 'A booking request needs an offered slotId or a preference',
});
export type BookingRequestedEvent = z.infer<typeof BookingRequestedEventSchema>;

export const BookingRequestedResponseSchema = z.object({
  received: z.literal(true),
  duplicate: z.boolean(),
  bookingRequestId: IdSchema,
  /** Always pending: an AI request is confirmed by staff. */
  status: z.literal('pending'),
  taskId: IdSchema.nullable(),
  /** What to tell the caller. Never "you're booked". */
  say: z.string(),
});
export type BookingRequestedResponse = z.infer<typeof BookingRequestedResponseSchema>;

/** `POST /integration/events`: any event the service reports. */
export const InboundEventSchema = z.union([CallCompletedEventSchema, BookingRequestedEventSchema]);
export type InboundEvent = z.infer<typeof InboundEventSchema>;
export const InboundEventResponseSchema = z.union([
  CallCompletedResponseSchema,
  BookingRequestedResponseSchema,
]);

export const AvailabilityQuerySchema = FindSlotsRequestSchema.extend({
  id: IdSchema,
  type: z.literal('availability.query'),
  occurredAt: InstantSchema,
  externalCallId: ExternalCallIdSchema.nullable().default(null),
});
export type AvailabilityQuery = z.infer<typeof AvailabilityQuerySchema>;
export const AvailabilityResponseSchema = FindSlotsResponseSchema;

export const IntegrationStateSchema = z.enum(['not_connected', 'waiting_for_service', 'receiving']);
export type IntegrationState = z.infer<typeof IntegrationStateSchema>;

/** Staff view of one office's connection to the receptionist service. */
export const IntegrationViewSchema = z.object({
  officeId: IdSchema,
  state: IntegrationStateSchema,
  /** Plain words for the state, shown as is. */
  stateText: z.string(),
  serviceTenantId: z.string().nullable(),
  secretHint: z.string().nullable(),
  secretIssuedAt: InstantSchema.nullable(),
  /** The previous secret still verifies until then (rotation overlap). */
  previousSecretValidUntil: InstantSchema.nullable(),
  lastEventAt: InstantSchema.nullable(),
  lastEventType: z.string().nullable(),
  eventsLast7Days: z.number().int(),
  /** Paths under the API base (`/v1`). */
  endpoints: z.object({
    events: z.string(),
    availability: z.string(),
    bookingRequests: z.string(),
    /** Signed pull of the office's settings (remote control). */
    control: z.string(),
  }),
  /** Whether the server can keep signing secrets (VELORA_SECRETS_KEY is set). */
  secretStoreReady: z.boolean(),
});
export type IntegrationView = z.infer<typeof IntegrationViewSchema>;

export const IntegrationPatchSchema = z.object({
  serviceTenantId: ServiceTenantIdSchema.nullable(),
});
export type IntegrationPatch = z.infer<typeof IntegrationPatchSchema>;

/** Issued once; only the hint is stored readable. */
export const IntegrationSecretSchema = z.object({
  officeId: IdSchema,
  secret: z.string(),
  hint: z.string(),
  previousSecretValidUntil: InstantSchema.nullable(),
});
export type IntegrationSecret = z.infer<typeof IntegrationSecretSchema>;

/* ------------------------------------------------------------------------ */
/* Remote control: Velora drives recep.client (2026-10-20)                   */
/* ------------------------------------------------------------------------ */
/*
 * The office's receptionist settings live in Velora; the receptionist
 * service (recep.client, LXC 232) PULLS them with a signed request
 * (`POST /v1/receptionist/integration/control`, same HMAC as the events) and
 * applies them to its tenant. Velora never pushes into the service. What the
 * AI may do is enforced twice: the service is told, and the action bridge
 * refuses a turned-off action server-side.
 */

/** Bridge actions an office can turn on or off (lookup/verify/office info are always on). */
export const REMOTE_ACTIONS = [
  'find_openings',
  'book',
  'confirm',
  'reschedule',
  'cancel_request',
  'appointments',
  'balance',
  'benefits',
] as const;
export const RemoteActionSchema = z.enum(REMOTE_ACTIONS);
export type RemoteAction = z.infer<typeof RemoteActionSchema>;

export const RemoteActionsSchema = z.object({
  /** Offer openings from the schedule. */
  find_openings: z.boolean(),
  /** File a booking REQUEST (staff approve; never written to the PMS by the AI). */
  book: z.boolean(),
  /** Confirm the verified caller's own visit. */
  confirm: z.boolean(),
  /** File a reschedule REQUEST. Never after hours. */
  reschedule: z.boolean(),
  /** File a cancel REQUEST for staff. Never after hours. */
  cancel_request: z.boolean(),
  /** Read the verified caller's upcoming visits. */
  appointments: z.boolean(),
  /** Say the balance: only after date of birth AND first name match (server-checked). */
  balance: z.boolean(),
  /** Say remaining benefits: same identity check as the balance. */
  benefits: z.boolean(),
});
export type RemoteActions = z.infer<typeof RemoteActionsSchema>;

/** Safe defaults for an office that has not chosen yet: requests only, no money talk. */
export const DEFAULT_REMOTE_ACTIONS: RemoteActions = {
  find_openings: true,
  book: true,
  confirm: true,
  reschedule: true,
  cancel_request: true,
  appointments: true,
  balance: false,
  benefits: false,
};

/** Never after hours (owner rule 2026-10-04: no cancels or reschedules after hours). */
export const AFTER_HOURS_FORBIDDEN: readonly RemoteAction[] = ['reschedule', 'cancel_request'];

export function afterHoursActions(a: RemoteActions): RemoteActions {
  const out = { ...a };
  for (const k of AFTER_HOURS_FORBIDDEN) out[k] = false;
  return out;
}

/** When the AI answers: only outside office hours, or every call. */
export const AnswerWhenSchema = z.enum(['after_hours', 'always']);
export type AnswerWhen = z.infer<typeof AnswerWhenSchema>;

/** After hours: triage + on-call alert for urgent calls, or take a message only. */
export const AfterHoursModeSchema = z.enum(['triage', 'message_only']);
export type AfterHoursMode = z.infer<typeof AfterHoursModeSchema>;

export const TransferNumberSchema = z.object({
  label: z.string().trim().min(1).max(60),
  e164: E164Schema,
});
export type TransferNumber = z.infer<typeof TransferNumberSchema>;

/** Staff view of the office's remote-control settings. */
export const RemoteControlSchema = z.object({
  locationId: IdSchema,
  /** Saved; false until someone turns the line on. */
  lineEnabled: z.boolean(),
  /** What the service actually does: on only when saved on AND the profile is not paused. */
  lineEffective: z.boolean(),
  /** Where callers go while the line is off; null = a short message, then hang up. */
  offForwardE164: E164Schema.nullable(),
  answerWhen: AnswerWhenSchema,
  afterHoursMode: AfterHoursModeSchema,
  transferNumbers: z.array(TransferNumberSchema).max(5),
  actions: RemoteActionsSchema,
  /** Send the call transcript with each call summary (PHI, stored in Velora, audited). */
  sendTranscripts: z.boolean(),
  /** The recep.client office id this Velora office is linked to (Integration). */
  serviceTenantId: z.string().nullable(),
  version: z.number().int(),
  updatedAt: InstantSchema.nullable(),
  /** When the service last pulled the settings, and which version it got. */
  pulledAt: InstantSchema.nullable(),
  pulledVersion: z.number().int().nullable(),
  /** The service has the current version. */
  inSync: z.boolean(),
});
export type RemoteControl = z.infer<typeof RemoteControlSchema>;

export const RemoteControlPatchSchema = z
  .object({
    /** The version you read; 0 when the office has no settings row yet. */
    expectedVersion: z.number().int().nonnegative(),
    lineEnabled: z.boolean().optional(),
    offForwardE164: E164Schema.nullable().optional(),
    answerWhen: AnswerWhenSchema.optional(),
    afterHoursMode: AfterHoursModeSchema.optional(),
    transferNumbers: z.array(TransferNumberSchema).max(5).optional(),
    actions: RemoteActionsSchema.partial().optional(),
    sendTranscripts: z.boolean().optional(),
  })
  .strict();
export type RemoteControlPatch = z.infer<typeof RemoteControlPatchSchema>;

/** The signed pull body (the service's own request id; any uuid). */
export const RemoteConfigRequestSchema = z.object({
  id: IdSchema,
  /** The version the service already applied, if any (for the "in sync" badge). */
  appliedVersion: z.number().int().nonnegative().nullable().default(null),
});
export type RemoteConfigRequest = z.infer<typeof RemoteConfigRequestSchema>;

/** What the service applies. Nothing here is PHI. */
export const RemoteConfigSchema = z.object({
  officeId: IdSchema,
  serviceTenantId: z.string().nullable(),
  version: z.number().int(),
  generatedAt: InstantSchema,
  mode: ReceptionistModeSchema,
  /** Answer calls at all. False: forward to `offForwardE164`, or a short message. */
  lineEnabled: z.boolean(),
  offForwardE164: E164Schema.nullable(),
  answerWhen: AnswerWhenSchema,
  afterHoursMode: AfterHoursModeSchema,
  persona: z.object({ name: z.string(), greeting: z.string() }),
  office: z.object({
    name: z.string(),
    timezone: z.string(),
    /** Null when Velora has none: the service keeps its own. */
    phone: z.string().nullable(),
    address: z.string().nullable(),
    /** "Monday 8:00 AM to 5:00 PM"; empty when Velora has no hours (the service keeps its own). */
    hours: z.array(z.string()),
    closedDays: z.array(z.string()),
  }),
  transferNumbers: z.array(TransferNumberSchema),
  actions: z.object({ businessHours: RemoteActionsSchema, afterHours: RemoteActionsSchema }),
  /** Never true: no payments, refunds or card numbers by phone. */
  moneyMovement: z.literal(false),
  /** Post every finished call to Velora (Calls tab + Leads). */
  sendCallSummaries: z.literal(true),
  sendTranscripts: z.boolean(),
  emergencyKeywords: z.array(z.string()),
  maxCallMinutes: z.number().int(),
});
export type RemoteConfig = z.infer<typeof RemoteConfigSchema>;

/* ------------------------------------------------------------------------ */
/* Reception modes (GET /receptionist/modes)                                 */
/* ------------------------------------------------------------------------ */
//
// "AI Receptionist" is the module; today it is the after-hours VOICE
// receptionist (stratus-voice). The header and the sidebar say which modes
// are on per office, so nobody reads "AI" as texting or web chat
// (2026-11-01).

export const ReceptionModeStateSchema = z.enum(['on', 'test', 'off', 'not_set_up']);
export type ReceptionModeState = z.infer<typeof ReceptionModeStateSchema>;

export const ReceptionModeSchema = z.object({
  key: z.enum(['after_hours_phone', 'daytime_overflow', 'texting', 'web_chat']),
  /** "After-hours phone answering" */
  label: z.string(),
  state: ReceptionModeStateSchema,
  /** "On", "Test mode", "Off", "Not set up". */
  stateText: z.string(),
  /** When it answers: "When the office is closed (Mon-Thu 8:00 AM-5:00 PM open)". */
  hours: z.string().nullable(),
  /** The office numbers it covers (office numbers, not PHI). */
  numbers: z.array(z.string()),
  lastCallAt: InstantSchema.nullable(),
});
export type ReceptionMode = z.infer<typeof ReceptionModeSchema>;

export const OfficeReceptionModesSchema = z.object({
  locationId: IdSchema,
  officeName: z.string(),
  modes: z.array(ReceptionModeSchema),
});
export const ReceptionModesSchema = z.object({ offices: z.array(OfficeReceptionModesSchema) });
export type ReceptionModes = z.infer<typeof ReceptionModesSchema>;
